import { PropsWithChildren } from 'react';
import { useGetChatCredentials } from '@/hooks/chat/useGetChatCredentials';
import { Error } from '@/components/custom-error/custom-error';
import { NoConversationSetupYet } from '@/components/custom-chat/no-conversation-setup-yet';
import { NotInvited } from '@/components/custom-chat/not-invited';
import { AxiosError, isAxiosError } from 'axios';
import { AuthenticatedStreamWrapper } from './authenticated-stream-wrapper';
import { AuthProgressLoader } from '@/components/auth/auth-progress-loader';
import { useMsalAccount } from '@/auth/use-msal-account';

const getErrorStatus = (error: unknown): number | undefined =>
  isAxiosError(error) ? (error as AxiosError).response?.status : undefined;

export const StreamWrapper = ({ children }: PropsWithChildren<object>) => {
  const { activeAccount } = useMsalAccount();

  const { data: chatCredentials, error } = useGetChatCredentials({});

  if (!activeAccount) {
    // essentially show the login screen
    return children;
  }

  const errorStatus = getErrorStatus(error);

  // user exists but chat is not set up yet
  if (errorStatus === 409) {
    return <NoConversationSetupYet />;
  }

  // user email not recognized by the platform
  if (errorStatus === 404) {
    return <NotInvited />;
  }

  if (error) {
    return <Error />;
  }

  if (!chatCredentials) {
    return <AuthProgressLoader />;
  }

  return <AuthenticatedStreamWrapper children={children} chatCredentials={chatCredentials} />;
};
